/**
 * Home's date and time lines (TMMAPP-0152 §A), through `format.ts` in the person's `/me` zone: a
 * served civil date renders as received, an instant in the zone.
 */
import type { MeetingCalendarEntry } from '@/api/meetings';
import { formatCivilDate, formatInstant } from '@/i18n/format';

/** "Sep 15": a civil date as received, an instant in `timeZone`. */
export function shortDate(value: string, locale: string, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatCivilDate(value, locale, options);
  try {
    return formatInstant(value, locale, timeZone, options);
  } catch {
    return value;
  }
}

/** A meeting's "Sun, Oct 18" and "7:00 PM" in the viewer's zone. */
export function meetingDateTime(
  entry: MeetingCalendarEntry,
  locale: string,
  timeZone: string,
): { date: string; time: string } {
  return {
    date: formatInstant(entry.startsAt, locale, timeZone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }),
    time: formatInstant(entry.startsAt, locale, timeZone, { timeStyle: 'short' }),
  };
}

/** Whole minutes from start to end; null when the span isn't served or isn't positive. */
export function meetingMinutes(entry: MeetingCalendarEntry): number | null {
  const minutes = Math.round((Date.parse(entry.endsAt) - Date.parse(entry.startsAt)) / 60_000);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}
